/**
 * Validators for IP / TimeOfDay / CustomVariable conditions, which require
 * arrays of nested objects with their own required fields.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  CUSTOM_VARIABLE_MATCHER_VALUES,
  PREDICATE_VALUES,
} from '../enums';
import {
  isPlainObject,
  isStringArray,
  joinPath,
  pushError,
  requireNumber,
  requireString,
  type ValidationCtx,
} from '../primitives';

export function validateIpCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  const path = joinPath(basePath, 'ipV4Ranges');
  if (raw.ipV4Ranges === undefined) {
    pushError(ctx, path, 'missing_required', 'Missing required field "ipV4Ranges"');
  } else if (!Array.isArray(raw.ipV4Ranges)) {
    pushError(ctx, path, 'invalid_array', 'Expected array of IP ranges');
  } else {
    raw.ipV4Ranges.forEach((entry, idx) => {
      const p = joinPath(path, idx);
      if (!isPlainObject(entry)) {
        pushError(ctx, p, 'invalid_object', 'IP range must be an object');
        return;
      }
      requireString(ctx, entry, 'ipRangeStart', p);
      requireString(ctx, entry, 'ipRangeEnd', p);
    });
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

function validateTimeRef(
  ctx: ValidationCtx,
  v: unknown,
  basePath: string,
  field: string,
): void {
  const path = joinPath(basePath, field);
  if (v === undefined) {
    pushError(ctx, path, 'missing_required', `Missing required field "${field}"`);
    return;
  }
  if (!isPlainObject(v)) {
    pushError(ctx, path, 'invalid_object', `"${field}" must be an object`);
    return;
  }
  requireNumber(ctx, v, 'hours', path);
  requireNumber(ctx, v, 'minutes', path);
}

export function validateTimeOfDayCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  validateTimeRef(ctx, raw.startTime, basePath, 'startTime');
  validateTimeRef(ctx, raw.endTime, basePath, 'endTime');
  const tzPath = joinPath(basePath, 'timeZone');
  if (raw.timeZone === undefined) {
    pushError(ctx, tzPath, 'missing_required', 'Missing required field "timeZone"');
  } else if (!isPlainObject(raw.timeZone)) {
    pushError(ctx, tzPath, 'invalid_object', 'timeZone must be an object');
  } else {
    requireString(ctx, raw.timeZone, 'code', tzPath);
  }
  requireString(ctx, raw, 'predicate', basePath, PREDICATE_VALUES);
}

export function validateCustomVariableCondition(
  ctx: ValidationCtx,
  raw: Record<string, unknown>,
  basePath: string,
): void {
  const path = joinPath(basePath, 'values');
  if (raw.values === undefined) {
    pushError(ctx, path, 'missing_required', 'Missing required field "values"');
    return;
  }
  if (!Array.isArray(raw.values)) {
    pushError(ctx, path, 'invalid_array', 'Expected array of values');
    return;
  }
  raw.values.forEach((entry, idx) => {
    const p = joinPath(path, idx);
    if (!isPlainObject(entry)) {
      pushError(ctx, p, 'invalid_object', 'Value must be an object');
      return;
    }
    requireString(ctx, entry, 'predicate', p, PREDICATE_VALUES);
    requireString(ctx, entry, 'valueMatcher', p, CUSTOM_VARIABLE_MATCHER_VALUES);
    requireNumber(ctx, entry, 'variableIndex', p);
    if (!isStringArray(entry.variableValues)) {
      pushError(
        ctx,
        joinPath(p, 'variableValues'),
        'invalid_array',
        'Expected string[]',
      );
    }
  });
}
